import { useState } from "react";
import { CheckCircle2 } from "lucide-react";
import { cn } from "@/lib/utils";
import { useLanguage } from "@/contexts/LanguageContext";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import type { GeneratedImage } from "@/hooks/useGeneratedImages";

interface BaseballTrendPreviewHistoryProps {
  /** All past baseball_trend previews for the user, newest first. */
  images: GeneratedImage[];
  /** URLs of the current generation pair — excluded so we don't duplicate
   *  them between the main grid and the history strip. */
  excludeUrls: string[];
  /** Currently picked historical preview URL (null = nothing picked OR the
   *  user picked from the current pair instead). */
  selectedUrl: string | null;
  onSelect: (url: string) => void;
  /** Currently selected aspect ratio for the in-progress flow. Past previews
   *  with a different aspect_ratio are hidden — picking a 16:9 preview while
   *  the flow is set to 9:16 would force a letterboxed/cropped video render,
   *  which is the bug this filter prevents. */
  aspectRatio: string;
  /** How many history thumbnails to show inline before "Show more". */
  inlineCount?: number;
}

/** History picker for the Baseball Trend preview step. Renders up to
 *  `inlineCount` previously generated previews; a "Show more" button opens
 *  a dialog with everything else. */
export function BaseballTrendPreviewHistory({
  images,
  excludeUrls,
  selectedUrl,
  onSelect,
  aspectRatio,
  inlineCount = 2,
}: BaseballTrendPreviewHistoryProps) {
  const { t } = useLanguage();
  const [dialogOpen, setDialogOpen] = useState(false);

  const excluded = new Set(excludeUrls);
  const eligible = images.filter(
    (img) =>
      img.image_url &&
      !excluded.has(img.image_url) &&
      img.aspect_ratio === aspectRatio,
  );
  if (eligible.length === 0) return null;

  const inline = eligible.slice(0, inlineCount);
  const overflow = eligible.length - inline.length;

  /** Small inline thumbnail — square, ~96px. Compact so the strip doesn't
   *  compete with the main preview pair above it. */
  const renderInlineTile = (img: GeneratedImage) => {
    const url = img.image_url!;
    const isSelected = selectedUrl === url;
    return (
      <button
        key={img.id}
        type="button"
        onClick={() => onSelect(url)}
        aria-pressed={isSelected}
        className={cn(
          "relative w-20 h-20 md:w-24 md:h-24 shrink-0 rounded-xl overflow-hidden border-2 transition-all duration-200",
          isSelected
            ? "border-orange-500 ring-2 ring-orange-500/40 shadow-md"
            : "border-border hover:border-orange-500 hover:ring-1 hover:ring-orange-500/40 hover:shadow-sm",
        )}
      >
        <img
          src={url}
          alt="past preview"
          className="absolute inset-0 w-full h-full object-cover"
          loading="lazy"
        />
        {isSelected && (
          <div className="absolute top-1 end-1 w-5 h-5 rounded-full bg-orange-500 text-white flex items-center justify-center shadow">
            <CheckCircle2 className="w-3.5 h-3.5" />
          </div>
        )}
      </button>
    );
  };

  /** Larger thumbnail for the "Show more" dialog so the user can pick more
   *  confidently across the full history. Still square. */
  const renderDialogTile = (img: GeneratedImage) => {
    const url = img.image_url!;
    const isSelected = selectedUrl === url;
    return (
      <button
        key={img.id}
        type="button"
        onClick={() => {
          onSelect(url);
          setDialogOpen(false);
        }}
        aria-pressed={isSelected}
        className={cn(
          "relative aspect-square w-full rounded-xl overflow-hidden border-2 transition-all duration-200",
          isSelected
            ? "border-orange-500 ring-2 ring-orange-500/40 shadow-md"
            : "border-border hover:border-orange-500 hover:ring-2 hover:ring-orange-500/40 hover:shadow-sm",
        )}
      >
        <img
          src={url}
          alt="past preview"
          className="absolute inset-0 w-full h-full object-cover"
          loading="lazy"
        />
        {isSelected && (
          <div className="absolute top-2 end-2 w-6 h-6 rounded-full bg-orange-500 text-white flex items-center justify-center shadow">
            <CheckCircle2 className="w-4 h-4" />
          </div>
        )}
      </button>
    );
  };

  return (
    <div className="space-y-2 md:space-y-3">
      <div>
        <h4 className="text-sm font-semibold text-foreground">
          {t.baseballTrend.previewHistoryTitle}
        </h4>
        <p className="text-xs text-muted-foreground mt-0.5">
          {t.baseballTrend.previewHistoryHint}
        </p>
      </div>

      <div className="flex flex-wrap gap-2 md:gap-3 items-center">
        {inline.map(renderInlineTile)}
        {overflow > 0 && (
          <button
            type="button"
            onClick={() => setDialogOpen(true)}
            className="inline-flex items-center gap-1 px-3 h-20 md:h-24 rounded-xl text-xs font-medium bg-card border border-border hover:border-primary/40 hover:bg-card/80 transition-colors"
          >
            <span>
              {t.baseballTrend.previewHistoryShowMore} ({overflow})
            </span>
          </button>
        )}
      </div>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>{t.baseballTrend.previewHistoryDialogTitle}</DialogTitle>
          </DialogHeader>
          <div className="grid grid-cols-3 sm:grid-cols-4 gap-2 md:gap-3 max-h-[60vh] overflow-y-auto p-1">
            {eligible.map(renderDialogTile)}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
